import { ChangeEvent, useContext, useEffect, useMemo, useState } from 'react';
import GlobalStateContext from '@/components/global/GlobalState';
import { deleteAuthCookies } from '@/managers/Authentication/cognito';
import { PbsKidsGamesWebPage, PbsKidsPage, PbsKidsSeriesWebPage, PbsKidsVideo, PbsKidsVideoPlaylist, PbsKidsVideosWebPage } from '@/types/pbskids-graph';
import PageInfoView from './view';
import { GameData } from '@/components/GamePlayer';
import { Builds } from '@/pages/games/play/[...slugsIds]';

export enum PanelStates {
  closed = 'PANELS_CLOSED',
  info = 'INFO_PANEL',
  tools = 'TOOLS_PANEL',
  versions = 'VERSIONS_PANEL',
}

export type PageInfoProps = {
  pageData?: PbsKidsVideosWebPage | PbsKidsGamesWebPage | PbsKidsSeriesWebPage |
             PbsKidsVideoPlaylist | PbsKidsVideo | GameData,
  isCmsPreview?: boolean,
  drafts?: PbsKidsPage[],
  builds?: Builds,
};

export type PageCacheData = {
  age: string,
  ageDate: string,
  cacheControl: string,
  xCache: string,
};

export type CmsCacheData = null | {
  cmsCacheDate: string,
  cmsCacheAge: string,
  proxyCacheDate: string,
  proxyCacheAge: string,
  proxyCacheStatus: string,
}

export const getFriendlyDate = (timestampInSec?: string | number | null): string => {
  if (!timestampInSec) return 'N/A';

  if (typeof timestampInSec === 'string') {
    timestampInSec = parseInt(timestampInSec);
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).format(timestampInSec * 1000);
};

export default function PageInfo({ pageData, drafts, builds, isCmsPreview }: PageInfoProps) {
  const [ panelState, setPanelState ] = useState(PanelStates.closed);
  const [ pageCacheData, setPageCacheData ] = useState<PageCacheData>({ age: '0', ageDate: '', cacheControl: '', xCache: '' });
  const { showDevUI, setShowDevUI, showThemeDevUI, setShowThemeDevUI } = useContext(GlobalStateContext);
  const nowSec = useMemo(() => Math.round(Date.now() / 1000), []);

  const getAge = (timestampInSec?: string | null): string => {
    return !timestampInSec ? 'N/A' :
      (nowSec - parseInt(timestampInSec)).toString();
  };

  let draft;
  if (pageData && 'draftName' in pageData && pageData.draftName) {
    draft = pageData;
  }

  let build;
  if (pageData && 'build' in pageData) {
    build = pageData.build;
  }

  let cmsCacheData;
  if (pageData && '_meta' in pageData) {
    cmsCacheData = {
      cmsCacheDate: getFriendlyDate(pageData._meta?.cacheDetails?.cmsRenderedTimestamp),
      cmsCacheAge: getAge(pageData._meta?.cacheDetails?.cmsRenderedTimestamp),
      proxyCacheDate: getFriendlyDate(pageData._meta?.cacheDetails?.proxyCacheTimestamp),
      proxyCacheAge: getAge(pageData._meta?.cacheDetails?.proxyCacheTimestamp),
      proxyCacheStatus: pageData._meta?.cacheDetails?.proxyCacheStatus || 'N/A',
    };
  }

  const data = {
    build,
    builds,
    draft,
    drafts,
    isCmsPreview,
    pageCacheData,
    cmsCacheData,
  };

  const actions = {
    openPageInfoPanel: (state: PanelStates) => {
      setPanelState(state);
    },
    closePageInfoPanel: () => {
      setPanelState(PanelStates.closed);
    },
    toggleShowPerformanceCheck: (e: ChangeEvent) => {
      setShowDevUI((e.target as HTMLInputElement)?.checked);
    },
    toggleShowThemeDebugger: (e: ChangeEvent) => {
      setShowThemeDevUI((e.target as HTMLInputElement)?.checked);
    },
    logout: () => {
      deleteAuthCookies();
      window.location.reload();
    },
  };

  const states = {
    panelState,
    showDevUI,
    showThemeDevUI,
  };

  useEffect(() => {
    // Fetch the current page to read the headers on it since we can't
    // read the headers on the page we're loaded on via client side JS.
    fetch(window.location.href)
      .then((res) => {
        const age = res.headers.get('age') || '0';
        const ageDate = getFriendlyDate((nowSec - parseInt(age)).toString());
        const cacheControl = res.headers.get('cache-control') || '';
        const xCache = res.headers.get('x-cache') || '';

        setPageCacheData({
          age,
          ageDate,
          cacheControl,
          xCache,
        });
      });
  }, [ nowSec ]);

  return <PageInfoView data={data} states={states} actions={actions} />;
}
